import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Egg, TrendingUp, Activity, Calendar, Mail, Phone,
  MapPin, AlertTriangle,
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import { eggWatchAdminAPI, EggWatchFarmDetail as FarmDetailType } from '../../../services/eggWatchAdminAPI';
import './EggWatchAdmin.css';

const EggWatchFarmDetail: React.FC = () => {
  const { farmId } = useParams<{ farmId: string }>();
  const [farm, setFarm] = useState<FarmDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartRange, setChartRange] = useState(30);
  const navigate = useNavigate();

  useEffect(() => {
    if (farmId) loadFarm(parseInt(farmId));
  }, [farmId]);

  const loadFarm = async (id: number) => {
    try {
      setLoading(true);
      const res = await eggWatchAdminAPI.getFarmDetail(id);
      setFarm(res.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load farm details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="ewa-loading-state">Loading farm details...</div>;

  if (error || !farm) {
    return (
      <div className="egg-watch-admin">
        <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch/farms')}>
          <ArrowLeft size={16} /> Back to Farms
        </button>
        <div className="ewa-empty-state"><p>{error || 'Farm not found'}</p></div>
      </div>
    );
  }

  const chartData = farm.daily_logs
    .slice(-chartRange)
    .map(log => ({
      date: new Date(log.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      eggs: log.egg_count,
      layRate: log.lay_rate,
    }));

  const loc = farm.location;
  const locationStr = [loc.city, loc.state].filter(Boolean).join(', ');

  return (
    <div className="egg-watch-admin">
      <button className="ewa-back-button" onClick={() => navigate('/planner/egg-watch/farms')}>
        <ArrowLeft size={16} /> Back to Farms
      </button>

      {/* Farm Header */}
      <div className="ewa-farm-header">
        <div>
          <h1>{farm.name}</h1>
          {locationStr && (
            <div className="ewa-farm-location"><MapPin size={14} /> {locationStr}</div>
          )}
        </div>
        <div className="ewa-farm-contact-actions">
          {farm.email && (
            <a href={`mailto:${farm.email}`} className="ewa-btn-secondary">
              <Mail size={14} /> Email
            </a>
          )}
          {farm.phone && (
            <a href={`tel:${farm.phone}`} className="ewa-btn-secondary">
              <Phone size={14} /> Call
            </a>
          )}
        </div>
      </div>

      {/* Stats */}
      <div className="ewa-detail-stats-grid">
        <div className="ewa-detail-stat">
          <div className="ewa-stat-icon rate"><TrendingUp size={20} /></div>
          <div>
            <div className="ewa-stat-value">
              {farm.current_lay_rate != null ? `${farm.current_lay_rate.toFixed(1)}%` : '—'}
            </div>
            <div className="ewa-stat-label">Current Lay Rate</div>
            {farm.baseline_lay_rate != null && (
              <div className="ewa-baseline">Baseline: {farm.baseline_lay_rate.toFixed(1)}%</div>
            )}
          </div>
        </div>
        <div className="ewa-detail-stat">
          <div className="ewa-stat-icon hens"><Egg size={20} /></div>
          <div>
            <div className="ewa-stat-value">{farm.flocks.reduce((s, f) => s + f.quantity, 0).toLocaleString()}</div>
            <div className="ewa-stat-label">Total Hens</div>
          </div>
        </div>
        <div className="ewa-detail-stat">
          <div className="ewa-stat-icon logged"><Activity size={20} /></div>
          <div>
            <div className="ewa-stat-value">{farm.days_of_data}</div>
            <div className="ewa-stat-label">Days of Data</div>
          </div>
        </div>
        <div className="ewa-detail-stat">
          <div className="ewa-stat-icon farms"><Calendar size={20} /></div>
          <div>
            <div className="ewa-stat-value">{farm.flocks.length}</div>
            <div className="ewa-stat-label">Active Flocks</div>
          </div>
        </div>
      </div>

      {/* Production Chart */}
      <div className="ewa-card full-width">
        <div className="ewa-card-header">
          <h2><TrendingUp size={16} /> Production History</h2>
          <div className="ewa-chart-range-selector">
            {[7, 14, 30, 60].map(d => (
              <button
                key={d}
                className={chartRange === d ? 'active' : ''}
                onClick={() => setChartRange(d)}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>
        <div className="ewa-card-content">
          {chartData.length === 0 ? (
            <div className="ewa-empty-state">No production data yet</div>
          ) : (
            <div className="ewa-chart-container large">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="left" tick={{ fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" domain={[0, 100]} tick={{ fontSize: 12 }} unit="%" />
                  <Tooltip />
                  <Line yAxisId="left" type="monotone" dataKey="eggs" stroke="#8b0000" strokeWidth={2} dot={false} name="Eggs" />
                  <Line yAxisId="right" type="monotone" dataKey="layRate" stroke="#2563eb" strokeWidth={2} dot={false} name="Lay Rate %" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </div>

      {/* Detail Grid */}
      <div className="ewa-detail-grid">
        {/* Flocks */}
        <div className="ewa-card">
          <div className="ewa-card-header"><h2>Flocks</h2></div>
          <div className="ewa-card-content">
            {farm.flocks.length === 0 ? (
              <div className="ewa-empty-state">No flocks</div>
            ) : (
              farm.flocks.map(flock => (
                <div key={flock.id} className="ewa-flock-item">
                  <div>
                    <div className="ewa-flock-name">{flock.name}</div>
                    <div className="ewa-flock-meta">
                      {flock.breed_display || flock.breed || 'Unknown breed'}
                      {flock.age_weeks != null && ` · ${flock.age_weeks} weeks`}
                      {flock.production_phase && ` · ${flock.production_phase}`}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div><strong>{flock.quantity.toLocaleString()}</strong> hens</div>
                    {flock.peak_lay_rate != null && (
                      <div className="ewa-flock-meta">Peak: {flock.peak_lay_rate.toFixed(1)}%</div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Current Feed & Orders */}
        <div className="ewa-card">
          <div className="ewa-card-header"><h2>Feed & Orders</h2></div>
          <div className="ewa-card-content">
            {farm.current_feed && (
              <div style={{ marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase' }}>Current Feed</div>
                <div style={{ fontWeight: 600 }}>{farm.current_feed.formula_name || '—'}</div>
                {farm.current_feed.started_date && (
                  <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    Since {new Date(farm.current_feed.started_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </div>
                )}
              </div>
            )}
            <div style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
              Recent Orders
            </div>
            {farm.recent_orders.length === 0 ? (
              <div className="ewa-empty-state">No recent orders</div>
            ) : (
              farm.recent_orders.map(order => (
                <div key={order.id} className="ewa-order-item">
                  <div className="ewa-order-date">
                    {new Date(order.order_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                  <div className="ewa-order-summary">{order.items_summary}</div>
                  <div className="ewa-order-total">${order.total}</div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Alerts */}
        <div className="ewa-card">
          <div className="ewa-card-header">
            <h2><AlertTriangle size={16} /> Active Alerts</h2>
            <span className="ewa-status-badge yellow">{farm.alerts.length}</span>
          </div>
          <div className="ewa-card-content">
            {farm.alerts.length === 0 ? (
              <div className="ewa-empty-state">No active alerts</div>
            ) : (
              farm.alerts.map(alert => (
                <div key={alert.id} style={{ padding: '0.5rem 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className={`ewa-severity-badge ${alert.severity}`}>{alert.severity}</span>
                    <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                      {new Date(alert.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  <div style={{ marginTop: '0.25rem', fontSize: '0.875rem' }}>
                    {alert.alert_type.replace(/_/g, ' ')}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Insights */}
        <div className="ewa-card">
          <div className="ewa-card-header"><h2>Insights</h2></div>
          <div className="ewa-card-content">
            {farm.insights.length === 0 ? (
              <div className="ewa-empty-state">No insights available</div>
            ) : (
              farm.insights.map((insight, i) => (
                <div key={i} className={`ewa-insight-item ${insight.type}`}>
                  <div className="ewa-insight-title">{insight.title}</div>
                  <div className="ewa-insight-detail">{insight.detail}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Weather Events */}
      {farm.weather_events.length > 0 && (
        <div className="ewa-card full-width" style={{ marginTop: '1.5rem' }}>
          <div className="ewa-card-header"><h2>Weather Events</h2></div>
          <div className="ewa-card-content">
            <div className="ewa-table-container">
              <table className="ewa-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Event</th>
                    <th>Impact</th>
                  </tr>
                </thead>
                <tbody>
                  {farm.weather_events.map((evt, i) => (
                    <tr key={i}>
                      <td>{new Date(evt.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</td>
                      <td>{evt.event_type.replace(/_/g, ' ')}</td>
                      <td>
                        {evt.impact_pct != null ? (
                          <span className={evt.impact_pct < 0 ? 'ewa-negative' : 'ewa-positive'}>
                            {evt.impact_pct > 0 ? '+' : ''}{evt.impact_pct.toFixed(1)}%
                          </span>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EggWatchFarmDetail;
